import {
	type GetAverageReviewsForApplicationQuery,
	isWeb,
	type ReviewEdgeFragment,
} from "@stepapp/core";
import {
	Block,
	palette,
	SvgIcon,
	spacing,
	TestimonialItem,
	Typography,
	useBreakpoints,
} from "@stepapp/ui";
import { type Ref, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, FlatList, StyleSheet, View } from "react-native";
import { ServiceNotice } from "../../src/components";
import StarRatingItem from "./StarRatingItem";

type ReviewsListProps = {
	reviewItems: ReviewEdgeFragment[];
	loading: boolean;
	avgAppReviews?: GetAverageReviewsForApplicationQuery["getAverageReviewsForApplication"];
	handleOnEndReached?: () => void;
	footerRef?: Ref<View>;
};

const keyExtractor = ({ cursor }: { cursor: string }) => cursor;

const ReviewsList = ({
	reviewItems,
	loading,
	avgAppReviews,
	handleOnEndReached,
	footerRef,
}: ReviewsListProps) => {
	const { t } = useTranslation();
	const { sm, md, height } = useBreakpoints();

	const numColumns = md ? 3 : 1;

	const starRatings = useMemo(() => {
		return [
			{ stars: 5, count: avgAppReviews?.fiveStarCount || 0 },
			{ stars: 4, count: avgAppReviews?.fourStarCount || 0 },
			{ stars: 3, count: avgAppReviews?.threeStarCount || 0 },
			{ stars: 2, count: avgAppReviews?.twoStarCount || 0 },
			{ stars: 1, count: avgAppReviews?.oneStarCount || 0 },
		];
	}, [avgAppReviews]);

	const showFooterLoader = reviewItems?.length > 0 && loading;

	const renderItem = useCallback(
		({ item, index }: { item: ReviewEdgeFragment; index: number }) => (
			<TestimonialItem
				containerStyle={styles.testimonialItem}
				key={`${item?.node.id}${index}`}
				review={item?.node}
				postedAt={item?.node?.postedAt}
				truncateComment={false}
				t={t}
			/>
		),
		[t],
	);

	const ListHeaderComponent = useCallback(
		() => (
			<Block marginBottom={2}>
				<Typography style={styles.headingText} variant="newH2">
					{t("screens.service.reviews")}
				</Typography>

				<Block
					row={md}
					style={[
						styles.ratingContainer,
						{ padding: spacing.unit * (md ? 4.5 : 2) },
					]}
				>
					<Block flex={1} alignCenter justifyCenter>
						<Typography
							variant="largeTitle"
							style={[styles.ratingText, sm && { fontSize: 35 }]}
						>
							{`${(avgAppReviews?.average || 0)?.toFixed(2)}/5`}
						</Typography>
						<Block row>
							{[...new Array(5)].map((_, i) => (
								<SvgIcon
									// biome-ignore lint/suspicious/noArrayIndexKey: reason
									key={`star-${i}`}
									color={palette.primary.accent}
									name="star"
									size={sm ? 20 : 18}
								/>
							))}
						</Block>

						<Typography style={styles.reviewsAmountText} variant="paragraph">
							{`${t("screens.reviews.numberOfReviews")}: ${avgAppReviews?.total || ""}+`}
						</Typography>
					</Block>

					<Block flex={1} alignCenter style={sm && styles.starRatingContainer}>
						{starRatings?.map(({ stars, count }) => (
							<StarRatingItem
								key={stars}
								stars={stars}
								count={count}
								totalReviews={avgAppReviews?.total || 0}
							/>
						))}
					</Block>
				</Block>

				<ServiceNotice type="review" containerStyles={styles.serviceNotice} />
			</Block>
		),
		[avgAppReviews, sm, md, starRatings, t],
	);

	const ListEmptyComponent = useCallback(
		() => (
			<Block>
				{loading ? (
					<ActivityIndicator
						color={palette.primary.accent}
						size="large"
						style={styles.loader}
					/>
				) : (
					<Typography center variant="paragraph">
						{t("screens.reviews.noReviews")}
					</Typography>
				)}
			</Block>
		),
		[loading, t],
	);

	const ListFooterComponent = useCallback(
		() => (
			<Block marginTop={2} flex={1}>
				{showFooterLoader && (
					<ActivityIndicator
						color={palette.primary.accent}
						size="large"
						style={styles.loader}
					/>
				)}
			</Block>
		),
		[showFooterLoader],
	);

	return (
		<>
			<FlatList
				ListHeaderComponent={ListHeaderComponent}
				ListEmptyComponent={ListEmptyComponent}
				ListFooterComponent={ListFooterComponent}
				key={numColumns}
				numColumns={numColumns}
				contentContainerStyle={styles.container}
				columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : null}
				data={reviewItems}
				keyExtractor={keyExtractor}
				renderItem={renderItem}
				onEndReached={handleOnEndReached}
				onEndReachedThreshold={0.5}
				style={{ flex: 1, height }}
			/>

			{footerRef && <View ref={footerRef} />}
		</>
	);
};

export default ReviewsList;

const styles = StyleSheet.create({
	container: {
		paddingBottom: spacing.unit * (isWeb ? 0 : 12),
		gap: 32,
	},
	columnWrapper: {
		gap: 32,
	},
	loader: {
		alignItems: "center",
		flex: 1,
		justifyContent: "center",
	},
	headingText: {
		marginTop: spacing.unit * (isWeb ? 4 : 2),
		marginBottom: spacing.unit * 3,
	},
	testimonialItem: {
		flex: 1,
		borderRadius: 8,
	},
	ratingContainer: {
		borderRadius: spacing.radius * 2,
		borderWidth: spacing.border,
		borderColor: palette.secondary.grey,
	},
	ratingText: {
		marginBottom: spacing.unit,
	},
	reviewsAmountText: {
		marginTop: spacing.unit * 1.5,
		marginBottom: spacing.unit * 2,
	},
	serviceNotice: {
		marginTop: spacing.unit * 4,
	},
	starRatingContainer: {
		margin: "auto",
	},
});
